import { useMemo, useState } from "react";

import { ReactComponent as ArrowIcon } from "../../assets/images/vector_arrow.svg";
import tableStyles from "../../styles/components/templates/TemplatesTable.module.css";
import IconButton from "../../ui/buttons/IconButton";
import cardStyles from "../../ui/cards/TemplateCards.module.css";
import { statusInfo } from "../../utils/whatsappTemplateDraft";

import styles from "./WhatsAppTemplates.module.css";

export const GROUP_COLUMNS = "minmax(200px, 2fr) 120px 120px 120px 130px";
export const UNGROUPED_GROUP_NAME = "Sin agrupar";

const headerCellStyle = {
  textAlign: "center",
  color: "var(--color-text-text-primary)",
  fontSize: 14,
  fontFamily: "DM Sans",
  fontWeight: 500,
  lineHeight: "17px",
  minWidth: 0,
  whiteSpace: "nowrap",
  background: "transparent",
  border: "none",
  cursor: "pointer",
};

export const countByTone = (templates, tone) =>
  templates.filter((template) => statusInfo(template.status).tone === tone).length;

const SORT_VALUES = {
  name: (group) => group.name,
  total: (group) => group.templates.length,
  approved: (group) => countByTone(group.templates, "success"),
  pending: (group) => countByTone(group.templates, "pending"),
  attention: (group) => countByTone(group.templates, "danger"),
};

const HEADERS = [
  { key: "name", label: "Grupo", align: "left" },
  { key: "total", label: "Plantillas" },
  { key: "approved", label: "Aprobadas" },
  { key: "pending", label: "En revisión" },
  { key: "attention", label: "Con problemas" },
];

function HeaderCell({ children, column, sortKey, sortConfig, onSort, align = "center" }) {
  const isActive = sortConfig.key === sortKey;
  const directionLabel = sortConfig.direction === "asc" ? "↑" : "↓";
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      style={{ ...headerCellStyle, gridColumn: column, textAlign: align }}
    >
      {children} {isActive ? directionLabel : ""}
    </button>
  );
}

const compare = (left, right) =>
  typeof left === "number" && typeof right === "number"
    ? left - right
    : String(left).localeCompare(String(right), "es", { sensitivity: "base" });

const WhatsAppTemplateGroupsTable = ({ groups, onOpen, onEdit }) => {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((previous) => ({
      key,
      direction: previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedGroups = useMemo(() => {
    const valueOf = SORT_VALUES[sortConfig.key] || SORT_VALUES.name;
    const named = groups.filter((group) => group.id != null);
    const ungrouped = groups.filter((group) => group.id == null);
    const sorted = [...named].sort((left, right) => compare(valueOf(left), valueOf(right)));
    return [...(sortConfig.direction === "asc" ? sorted : sorted.reverse()), ...ungrouped];
  }, [groups, sortConfig]);

  return (
    <div className={tableStyles.templatesTableContainer}>
      <div className={tableStyles.templatesTable} style={{ "--templates-table-columns": GROUP_COLUMNS }}>
        <div className={tableStyles.headerContainer}>
          <div className={tableStyles.headerBody}>
            <div className={tableStyles.headerGrid}>
              {HEADERS.map((header, index) => (
                <HeaderCell
                  key={header.key}
                  column={String(index + 1)}
                  sortKey={header.key}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  align={header.align}
                >
                  {header.label}
                </HeaderCell>
              ))}
            </div>
          </div>
          <div className={tableStyles.headerActionSpacer} />
        </div>

        {sortedGroups.map((group) => {
          const open = () => onOpen?.(group);
          const editable = group.id != null && Boolean(onEdit);
          const rowClick = editable ? () => onEdit(group) : open;
          return (
            <div className={cardStyles.rowWrapper} key={group.id ?? "ungrouped"}>
              <div
                className={`${cardStyles.rowContent} ${cardStyles.attachmentRowContent}`}
                onClick={rowClick}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    rowClick();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={editable ? `Editar grupo ${group.name}` : `Grupo ${group.name}`}
                style={{ cursor: "pointer" }}
              >
                <div className={`${cardStyles.cell} ${styles.nameCell}`}>
                  <span className={styles.name}>{group.name}</span>
                  {group.id == null && <span className={styles.excerpt}>Plantillas que no están en ningún grupo</span>}
                </div>
                <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 2 }}>
                  {group.templates.length}
                </div>
                <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 3 }}>
                  {countByTone(group.templates, "success")}
                </div>
                <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 4 }}>
                  {countByTone(group.templates, "pending")}
                </div>
                <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 5 }}>
                  {countByTone(group.templates, "danger")}
                </div>
              </div>

              <div className={cardStyles.actionButtonWrapper}>
                <IconButton
                  icon={<ArrowIcon />}
                  size="md"
                  iconSize={14}
                  onClick={(event) => {
                    event.stopPropagation();
                    open();
                  }}
                  aria-label={`Abrir grupo ${group.name}`}
                  className={cardStyles.actionButton}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhatsAppTemplateGroupsTable;
